/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase typed boundary */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, ExternalLink, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  workspaceId: string;
  value: string[]; // topic names
  onChange: (names: string[]) => void;
}

type FrameworkTopic = { id: string; name: string; framework_column_id: string };
type FrameworkColumn = { id: string; name: string; position: number };

/**
 * Codex-driven Topics picker.
 * Source of truth: `content_framework_topics` for the active workspace.
 * Users cannot create topics here — only pick from the Codex Framework Topic Tree.
 * See mem://features/topics-are-codex-driven.md.
 */
export function CodexTopicPicker({ workspaceId, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [topics, setTopics] = useState<FrameworkTopic[]>([]);
  const [columns, setColumns] = useState<FrameworkColumn[]>([]);

  useEffect(() => {
    if (!workspaceId) return;
    void (async () => {
      const [topicsRes, columnsRes] = await Promise.all([
        supabase
          .from("content_framework_topics")
          .select("id, label, framework_column_id")
          .eq("workspace_id", workspaceId)
          .order("position", { ascending: true }),
        supabase
          .from("content_framework_columns")
          .select("id, label, position")
          .eq("workspace_id", workspaceId)
          .order("position", { ascending: true }),
      ]);
      setTopics(((topicsRes.data ?? []) as any[]).map((r) => ({
        id: r.id,
        name: r.label,
        framework_column_id: r.framework_column_id,
      })));
      setColumns(((columnsRes.data ?? []) as any[]).map((r) => ({
        id: r.id,
        name: r.label,
        position: r.position,
      })));
    })();
  }, [workspaceId]);

  const norm = (s: string) => s.trim().toLowerCase();
  const isSelected = (name: string) => value.some((v) => norm(v) === norm(name));

  const toggle = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (isSelected(trimmed)) {
      onChange(value.filter((v) => norm(v) !== norm(trimmed)));
    } else {
      onChange([...value, trimmed]);
    }
    setQuery("");
  };

  const remove = (name: string) => onChange(value.filter((v) => v !== name));

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matchTopics = q
      ? topics.filter((t) => t.name.toLowerCase().includes(q))
      : topics;
    return columns
      .map((col) => ({
        column: col,
        topics: matchTopics.filter((t) => t.framework_column_id === col.id),
      }))
      .filter((g) => g.topics.length > 0);
  }, [columns, topics, query]);

  const totalMatched = grouped.reduce((acc, g) => acc + g.topics.length, 0);

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((name) => (
            <Badge key={name} variant="secondary" className="gap-1 pr-1">
              {name}
              <button
                type="button"
                onClick={() => remove(name)}
                className="rounded-sm p-0.5 hover:bg-muted-foreground/20"
                aria-label={`Remove ${name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal text-muted-foreground"
          >
            {value.length > 0 ? `${value.length} selected` : "Add topics…"}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search topics…"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {totalMatched === 0 ? (
                <CommandEmpty>
                  {topics.length === 0
                    ? "No topics in this workspace's Codex Topic Tree yet."
                    : "No matching topics."}
                </CommandEmpty>
              ) : (
                grouped.map((g) => (
                  <CommandGroup key={g.column.id} heading={g.column.name}>
                    {g.topics.map((t) => {
                      const selected = isSelected(t.name);
                      return (
                        <CommandItem key={t.id} value={t.name} onSelect={() => toggle(t.name)}>
                          <Check
                            className={cn("mr-2 h-4 w-4", selected ? "opacity-100" : "opacity-0")}
                          />
                          {t.name}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))
              )}
            </CommandList>
            <div className="border-t border-border p-1">
              <Link
                to="/composer?bottomDock=codex&codexTab=framework"
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <ExternalLink className="h-3 w-3" />
                Manage in Codex → Framework
              </Link>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
